import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Clock, Calendar, Tag } from 'lucide-react';
import { BlogPost as BlogPostType } from '../data/blogPosts';
import { fetchPost } from '../services/blogService';

// Post data injected by the blog-publisher Lambda at publish time.
// Allows React to render immediately without an API call when landing
// directly on a pre-rendered blog page hosted on S3.
declare global {
  interface Window {
    __BLOG_POST__?: BlogPostType;
  }
}

const BlogPost: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const [post, setPost] = useState<BlogPostType | undefined | null>(undefined);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!slug) return;

    // If the Lambda pre-rendered this page it will have injected window.__BLOG_POST__.
    // Use it directly so we never need an API call for statically published posts.
    if (window.__BLOG_POST__ && window.__BLOG_POST__.slug === slug) {
      setPost(window.__BLOG_POST__);
      setLoading(false);
      return;
    }

    fetchPost(slug).then((data) => {
      setPost(data ?? null);
      setLoading(false);
    });
  }, [slug]);

  useEffect(() => {
    if (post && slug) {
      // Update document title
      document.title = `${post.title} | FretDev Blog`;
      
      // Update or create Open Graph meta tags
      const updateMetaTag = (property: string, content: string) => {
        let tag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('property', property);
          document.head.appendChild(tag);
        }
        tag.content = content;
      };

      const url = `https://fretdev.com/blog/${slug}`;
      // Use SEO metadata if available, fall back to socialSnippets or title
      const ogTitle = post.seoMetadata?.ogTitle || post.socialSnippets?.headline || post.title;
      const ogDescription = post.seoMetadata?.ogDescription || post.excerpt;

      updateMetaTag('og:title', ogTitle);
      updateMetaTag('og:description', ogDescription);
      updateMetaTag('og:url', url);
      updateMetaTag('og:type', 'article');
      updateMetaTag('og:site_name', 'FretDev');
      updateMetaTag('og:image', 'https://fretdev.com/logo512.png');

      // Twitter Card tags
      const updateTwitterTag = (name: string, content: string) => {
        let tag = document.querySelector(`meta[name="${name}"]`) as HTMLMetaElement;
        if (!tag) {
          tag = document.createElement('meta');
          tag.setAttribute('name', name);
          document.head.appendChild(tag);
        }
        tag.content = content;
      };

      updateTwitterTag('twitter:card', 'summary_large_image');
      updateTwitterTag('twitter:title', ogTitle);
      updateTwitterTag('twitter:description', ogDescription);
      updateTwitterTag('twitter:image', 'https://fretdev.com/logo512.png');
    }
    
    // Cleanup: restore default title when component unmounts
    return () => {
      document.title = 'FretDev | Cloud Architecture & Generative AI Solutions';
    };
  }, [post, slug]);

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px' }}>
        <p style={{ color: 'var(--color-gray-400)', fontSize: '1.1rem' }}>Loading article...</p>
      </div>
    );
  }

  if (!post) {
    return (
      <div style={{ textAlign: 'center', padding: '120px 24px' }}>
        <h1 style={{ fontSize: '2rem', fontWeight: 800, marginBottom: '16px' }}>Post Not Found</h1>
        <p style={{ color: 'var(--color-gray-500)', marginBottom: '32px' }}>The article you're looking for doesn't exist.</p>
        <Link
          to="/blog"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            padding: '12px 24px',
            background: 'linear-gradient(135deg, #F59E0B, #D97706)',
            color: '#0F172A',
            borderRadius: '12px',
            fontWeight: 700,
            textDecoration: 'none',
          }}
        >
          <ArrowLeft size={16} /> Back to Blog
        </Link>
      </div>
    );
  }

  return (
    <div>
      {/* ─── Hero Banner ─── */}
      <section
        className="hero-fade-in hero-padding"
        style={{
          background: post.gradient,
          borderRadius: '24px',
          marginBottom: '48px',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <div style={{ position: 'relative', zIndex: 1 }}>
          <Link
            to="/blog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              color: 'rgba(255,255,255,0.8)',
              textDecoration: 'none',
              fontSize: '0.9rem',
              fontWeight: 500,
              marginBottom: '24px',
              transition: 'color 0.2s',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#fff'; }}
            onMouseLeave={(e) => { e.currentTarget.style.color = 'rgba(255,255,255,0.8)'; }}
          >
            <ArrowLeft size={16} /> Back to Blog
          </Link>

          <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px', flexWrap: 'wrap' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                padding: '5px 14px',
                background: 'rgba(255,255,255,0.2)',
                backdropFilter: 'blur(8px)',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: 600,
              }}
            >
              <Tag size={13} /> {post.category}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' }}>
              <Calendar size={14} /> {post.date}
            </span>
            <span style={{ display: 'flex', alignItems: 'center', gap: '5px', color: 'rgba(255,255,255,0.75)', fontSize: '0.85rem' }}>
              <Clock size={14} /> {post.readTime} min read
            </span>
          </div>

          <h1
            style={{
              fontSize: 'clamp(1.8rem, 4vw, 2.8rem)',
              fontWeight: 800,
              fontFamily: 'var(--font-heading)',
              color: '#fff',
              lineHeight: 1.2,
              maxWidth: '700px',
            }}
          >
            {post.title}
          </h1>
        </div>
      </section>

      {/* ─── Key Takeaways ─── */}
      {post.keyTakeaways && post.keyTakeaways.length > 0 && (
        <div
          style={{
            maxWidth: '740px',
            margin: '0 auto 48px',
            padding: '24px 28px',
            background: 'var(--color-gray-50)',
            borderLeft: '4px solid var(--color-secondary)',
            borderRadius: '4px 12px 12px 4px',
          }}
        >
          <p
            style={{
              fontSize: '0.78rem',
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--color-secondary-dark)',
              marginBottom: '14px',
            }}
          >
            Key Takeaways
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
            {post.keyTakeaways.map((point, i) => (
              <li
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '10px',
                  fontSize: '1rem',
                  lineHeight: 1.6,
                  color: 'var(--color-gray-700)',
                }}
              >
                <span style={{ color: 'var(--color-secondary)', fontWeight: 700, marginTop: '2px', flexShrink: 0 }}>•</span>
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* ─── Article Body ─── */}
      <article
        style={{
          maxWidth: '740px',
          margin: '0 auto',
          paddingBottom: '80px',
        }}
      >
        {post.content.map((block, i) => {
          if (block.startsWith('## ')) {
            return (
              <h2
                key={i}
                style={{
                  fontSize: '1.4rem',
                  fontWeight: 700,
                  fontFamily: 'var(--font-heading)',
                  color: 'var(--color-gray-800)',
                  marginTop: '40px',
                  marginBottom: '16px',
                  paddingTop: '16px',
                  borderTop: i > 1 ? '1px solid var(--color-gray-200)' : 'none',
                }}
              >
                {block.replace('## ', '')}
              </h2>
            );
          }
          if (block.startsWith('- ')) {
            const isFirstBullet = i === 0 || !post.content[i - 1]?.startsWith('- ');
            const isLastBullet = i === post.content.length - 1 || !post.content[i + 1]?.startsWith('- ');
            return (
              <div
                key={i}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '12px',
                  fontSize: '1.05rem',
                  lineHeight: 1.7,
                  color: 'var(--color-gray-700)',
                  marginTop: isFirstBullet ? '4px' : '0',
                  marginBottom: isLastBullet ? '24px' : '10px',
                  paddingLeft: '8px',
                }}
              >
                <span style={{ color: 'var(--color-primary)', fontWeight: 700, marginTop: '2px', flexShrink: 0 }}>•</span>
                <span>{block.slice(2)}</span>
              </div>
            );
          }
          return (
            <p
              key={i}
              style={{
                fontSize: '1.08rem',
                lineHeight: 1.85,
                color: 'var(--color-gray-600)',
                marginBottom: '20px',
              }}
            >
              {block}
            </p>
          );
        })}

        {/* ─── Bottom Nav ─── */}
        <div
          style={{
            marginTop: '56px',
            paddingTop: '32px',
            borderTop: '1px solid var(--color-gray-200)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: '16px',
          }}
        >
          <Link
            to="/blog"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: 'var(--color-primary)',
              fontWeight: 600,
              textDecoration: 'none',
              fontSize: '0.95rem',
            }}
          >
            <ArrowLeft size={16} /> All Articles
          </Link>
          <Link
            to="/contact"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              padding: '12px 24px',
              background: 'linear-gradient(135deg, #F59E0B, #D97706)',
              color: '#0F172A',
              borderRadius: '12px',
              fontWeight: 700,
              textDecoration: 'none',
              fontSize: '0.95rem',
              transition: 'transform 0.25s, box-shadow 0.25s',
              boxShadow: '0 4px 15px rgba(245,158,11,0.3)',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 25px rgba(245,158,11,0.45)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 15px rgba(245,158,11,0.3)';
            }}
          >
            Discuss a Project
          </Link>
        </div>
      </article>
    </div>
  );
};

export default BlogPost;
